import clsx from 'clsx';
import { ComponentProps } from 'preact/compat';
import { useState } from 'preact/hooks';

import { cn } from '@/lib/utils';

// Size variants
const sizeClasses = {
  small: {
    container: 'w-11 h-6',
    thumb: 'size-5',
    translate: 'translate-x-[1.375rem]',
    label: 'text-[7px] font-medium w-6',
  },
  medium: {
    container: 'w-14 h-7',
    thumb: 'size-6',
    translate: 'translate-x-[1.875rem]',
    label: 'text-[10px] font-bold w-8',
  },
  large: {
    container: 'w-16 h-8',
    thumb: 'size-7',
    translate: 'translate-x-[2.125rem]',
    label: 'text-xs font-bold w-9',
  },
} as const;

interface SwitchControlProps extends Omit<ComponentProps<'button'>, 'onChange' | 'type' | 'size'> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  size?: keyof typeof sizeClasses;
  showOnOffLabel?: boolean;
}

const SwitchControl = ({
  checked,
  onChange,
  disabled = false,
  size = 'medium',
  className,
  showOnOffLabel,
  ...buttonProps
}: SwitchControlProps) => {
  const isControlled = checked != null;
  const [isCheckedState, setIsCheckedState] = useState(false);
  const isChecked = isControlled ? checked : isCheckedState;

  const handleToggle = () => {
    if (disabled) return;

    const newValue = !isChecked;
    if (isControlled) {
      onChange?.(newValue);
    } else {
      setIsCheckedState(newValue);
    }
  };

  const currentSize = sizeClasses[size];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isChecked}
      disabled={disabled}
      onClick={handleToggle}
      className={cn(
        'preflight preflight-button relative inline-flex items-center rounded-full transition-colors duration-200 ease-in-out',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'bg-[#DFDFDF] aria-checked:bg-primary-green',
        currentSize.container,
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
    >
      <span
        className={cn(
          'inline-block transform rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out',
          currentSize.thumb,
          isChecked ? currentSize.translate : 'translate-x-0.5',
        )}
      />
      {!showOnOffLabel ? (
        <span className="sr-only">{isChecked ? 'Turn off' : 'Turn on'}</span>
      ) : isChecked ? (
        <span className={clsx(currentSize.label, 'absolute left-0 animate-in fade-in')}>ON</span>
      ) : (
        <span className={clsx(currentSize.label, 'absolute right-0 animate-in fade-in')}>OFF</span>
      )}
    </button>
  );
};

export default SwitchControl;
